import { ButtonProps, Button as MuiButton, SxProps } from '@mui/material';
import { HTMLAttributeAnchorTarget } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getLinkAttributes } from '../../utils/tools';

// Style for all button types
const BUTTON_BASE_STYLE: SxProps = {
  borderWidth: '2px',
  '&:hover': {
    borderWidth: '2px'
  },
  'svg.MuiSvgIcon-root': {
    fontSize: '1.5rem'
  }
};

// Style per button types
const BUTTON_STYLES: { [key in NonNullable<ButtonProps['variant']>]: SxProps } = {
  contained: {
    fontFamily: 'Open Sans Regular, Arial, sans-serif',
    py: '0.75rem',
    px: '1.25rem',
    border: '2px solid',
    borderColor: 'primary.main',
    '&:hover, &:focus-visible': {
      backgroundColor: '#FFFFFF',
      color: 'primary.main',
      borderColor: 'primary.main'
    }
  },
  outlined: {
    py: '0.75rem',
    px: '1.25rem'
  },
  text: {
    py: '0.5rem',
    px: '0.5rem'
  }
};

export type CustomButtonProps = ButtonProps & {
  to?: string;
  target?: HTMLAttributeAnchorTarget;
  isBig?: boolean;
  isActive?: boolean;
};

/**
 * Apply style properties depending on button color and button type
 */
const getButtonColorSx = (color: NonNullable<ButtonProps['color']>, variant: ButtonProps['variant']): SxProps => {
  if (variant === 'outlined' && color !== 'inherit') {
    return { borderColor: `${color}.main` };
  }
  return {};
};

export const Button = ({
  variant = 'text',
  color = 'primary',
  to,
  href,
  target,
  isBig,
  isActive,
  sx = {},
  ...props
}: CustomButtonProps) => {
  const bigStyle: SxProps = {
    fontFamily: 'Chewy, system-ui',
    fontSize: '1.75rem',
    lineHeight: '1.5',
    borderRadius: '2rem',
    width: '14rem',
    px: '2rem',
    py: '0.25rem',
    textAlign: 'center',
    ...(isActive && {
      backgroundColor: '#FFFFFF',
      color: 'primary.main'
    })
  }
  const buttonSx = {
    ...BUTTON_BASE_STYLE,
    ...getButtonColorSx(color, variant),
    ...BUTTON_STYLES[variant]
  };
  if(isBig) {
    Object.assign(buttonSx, bigStyle);
  }
  Object.assign(buttonSx, sx);

  return (
    <MuiButton
      {...props}
      disableElevation
      component={to ? RouterLink : 'button'}
      to={to}
      href={href}
      variant={variant}
      sx={buttonSx}
      {...getLinkAttributes(!to, target)}
    />
  );
};
